import React from 'react';
import { HashRouter, Routes, Route } from 'react-router-dom';

// Layouts
import { MainLayout } from './components/layout/MainLayout';
import { PublicLayout } from './components/layout/PublicLayout';
import { AuthenticatedRoute, RoleRoute } from './routes/AuthenticatedRoute';
import { ROLES } from './context/UserRoleContext';

// Public Pages
import { Home } from './pages/public/Home';
import { Booking } from './pages/public/Booking';
import { ForLeaders } from './pages/public/ForLeaders';
import { ForHR } from './pages/public/ForHR';
import { ForConsultants } from './pages/public/ForConsultants';
import { PublicCourses } from './pages/public/PublicCourses';
import { Contact } from './pages/public/Contact';
import { Login } from './pages/Login';
import AcademyActivate from './pages/AcademyActivate';

// Dashboard Pages
import { Dashboard } from './pages/Dashboard';
import { StrategicDashboard } from './pages/StrategicDashboard';
import { CustomerDashboard } from './pages/dashboards/CustomerDashboard';
import { ConsultantDashboard } from './pages/dashboards/ConsultantDashboard';
import { LeaderDashboard } from './pages/dashboards/LeaderDashboard';

// Engine & Logic Pages
import { OrganizationalHealthScore } from './pages/OrganizationalHealthScore';
import { InsightEngine } from './pages/InsightEngine';
import { CultureLeadershipProfile } from './pages/CultureLeadershipProfile';
import { DigitalTwin } from './pages/DigitalTwin';
import { DepartmentRiskAnalysis } from './pages/DepartmentRiskAnalysis';
import { DepartmentActionPlan } from './pages/DepartmentActionPlan';
import { AIActionEngine } from './pages/AIActionEngine';
import { ImprovementPlanEngine } from './pages/ImprovementPlanEngine';
import { WorkshopAgendaGenerator } from './pages/WorkshopAgendaGenerator';
import { BoardPresentationGenerator } from './pages/BoardPresentationGenerator';
import { LeaderActionEngine } from './pages/LeaderActionEngine';
import { InterventionOrchestrator } from './pages/InterventionOrchestrator';
import { WorkEnvironmentDashboard } from './pages/WorkEnvironmentDashboard';
import { OrganizationalHeatmap } from './pages/OrganizationalHeatmap';
import { ReportGenerator } from './pages/ReportGenerator';
import { AlertSystem } from './pages/AlertSystem';
import { ClientHealthOverview } from './pages/ClientHealthOverview';
import { WorkEnvironmentScore } from './pages/WorkEnvironmentScore';
import { WorkplaceStressIndex } from './pages/WorkplaceStressIndex';
import { PsychologicalSafetyIndex } from './pages/PsychologicalSafetyIndex';
import { ConflictRiskIndex } from './pages/ConflictRiskIndex';
import { AIDiagnosis } from './pages/AIDiagnosis';
import { ComplianceEngine } from './pages/ComplianceEngine';
import { NordicStandard } from './pages/NordicStandard';
import { Benchmark } from './pages/Benchmark';
import { Projects } from './pages/Projects';
import { Courses } from './pages/Courses';
import { AcademyAdminDashboard } from './pages/AcademyAdminDashboard';
import AcademyParticipantRegister from './pages/AcademyParticipantRegister';
import { Workshops } from './pages/Workshops';
import { WorkshopFacilitator } from './pages/WorkshopFacilitator';
import { LeaderCoach } from './pages/LeaderCoach';
import { Assessments } from './pages/Assessments';
import { TakeAssessment } from './pages/TakeAssessment';
import { ConflictAnalysis } from './pages/ConflictAnalysis';
import { IndustryProfiles } from './pages/IndustryProfiles';

// NEW UPGRADE PAGES
import { ConsultantToolkit } from './pages/ConsultantToolkit';
import { PsychosocialRiskMatrix } from './pages/PsychosocialRiskMatrix';
import { PlaceholderPage } from './pages/PlaceholderPage';

let appRenderDiagnosticLogged = false;
let loginRouteDiagnosticLogged = false;

function App() {
  if (!appRenderDiagnosticLogged) {
    appRenderDiagnosticLogged = true;
    console.info('[AUTH_BOOT_DIAG][App]', {
      point: 'app-render',
      appRendered: true
    });
  }

  if (!loginRouteDiagnosticLogged) {
    loginRouteDiagnosticLogged = true;
    console.info('[AUTH_BOOT_DIAG][App]', {
      point: 'login-route-registration',
      loginRoutePath: '/login',
      routeElementType: typeof Login
    });
  }

  return (
    <HashRouter>
      <Routes>
        {/* Public Website Routes */}
        <Route path="/" element={<PublicLayout />}>
          <Route index element={<Home />} />
          <Route path="booking" element={<Booking />} />
          <Route path="for-ledere" element={<ForLeaders />} />
          <Route path="for-hr" element={<ForHR />} />
          <Route path="for-konsulenter" element={<ForConsultants />} />
          <Route path="kurs-info" element={<PublicCourses />} />
          <Route path="kontakt" element={<Contact />} />
          <Route path="tjenester" element={<PlaceholderPage title="Våre Tjenester" description="Komplett oversikt over våre rådgivningstjenester." icon="Briefcase" />} />
        </Route>

        <Route path="/academy/activate" element={<AcademyActivate />} />
        <Route path="/login" element={<Login />} />

        {/* Standalone Assessment */}
        <Route path="/take-assessment" element={<TakeAssessment />} />

        {/* Protected Dashboard Routes */}
        <Route
          path="/dashboard"
          element={
            <AuthenticatedRoute>
              <MainLayout />
            </AuthenticatedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="main" element={<Dashboard />} />
          <Route path="customer" element={<CustomerDashboard />} />
          <Route path="consultant" element={<ConsultantDashboard />} />

          {/* Engine Modules */}
          <Route path="toolkit" element={<ConsultantToolkit />} />
          <Route path="risk-matrix" element={<PsychosocialRiskMatrix />} />
          <Route path="strategic" element={<StrategicDashboard />} />
          <Route path="orchestrator" element={<InterventionOrchestrator />} />
          <Route path="leader-actions" element={<LeaderActionEngine />} />
          <Route path="org-health" element={<OrganizationalHealthScore />} />
          <Route path="insight-engine" element={<InsightEngine />} />
          <Route path="culture-leadership" element={<CultureLeadershipProfile />} />
          <Route path="twin" element={<DigitalTwin />} />
          <Route path="dept-risk" element={<DepartmentRiskAnalysis />} />
          <Route path="dept-plan" element={<DepartmentActionPlan />} />
          <Route path="action-engine" element={<AIActionEngine />} />
          <Route path="90-day-plan" element={<ImprovementPlanEngine />} />
          <Route path="agenda-generator" element={<WorkshopAgendaGenerator />} />
          <Route path="board-presentation" element={<BoardPresentationGenerator />} />
          <Route path="heatmap" element={<OrganizationalHeatmap />} />
          <Route path="reports" element={<ReportGenerator />} />
          <Route path="alerts" element={<AlertSystem />} />
          <Route path="health" element={<ClientHealthOverview />} />
          <Route path="work-environment-score" element={<WorkEnvironmentScore />} />
          <Route path="stress-index" element={<WorkplaceStressIndex />} />
          <Route path="safety-index" element={<PsychologicalSafetyIndex />} />
          <Route path="conflict-risk" element={<ConflictRiskIndex />} />
          <Route path="diagnosis" element={<AIDiagnosis />} />
          <Route path="compliance" element={<ComplianceEngine />} />
          <Route path="nordic-standard" element={<NordicStandard />} />
          <Route path="benchmark" element={<Benchmark />} />
          <Route path="projects" element={<Projects />} />
          <Route path="courses" element={<Courses />} />
          <Route
            path="academy-admin"
            element={
              <RoleRoute roles={[ROLES.SUPERADMIN, ROLES.COMPANY_ADMIN]}>
                <AcademyAdminDashboard />
              </RoleRoute>
            }
          />
          <Route
            path="academy-admin/participants"
            element={
              <RoleRoute roles={[ROLES.SUPERADMIN, ROLES.COMPANY_ADMIN]}>
                <AcademyParticipantRegister />
              </RoleRoute>
            }
          />
          <Route path="workshops" element={<Workshops />} />
          <Route path="facilitator" element={<WorkshopFacilitator />} />
          <Route path="coach" element={<LeaderCoach />} />
          <Route path="assessments" element={<Assessments />} />
          <Route path="conflict" element={<ConflictAnalysis />} />
          <Route
            path="industry-profiles"
            element={
              <RoleRoute role={ROLES.SUPERADMIN}>
                <IndustryProfiles />
              </RoleRoute>
            }
          />
        </Route>
      </Routes>
    </HashRouter>
  );
}

export default App;